import { useState } from 'react';
import { Table, Input, Select, Button, Alert, useToast } from '@dreamtree-org/twreact-ui';
import { UserPlus, Copy } from 'lucide-react';
import { SectionCard, QueryState, StatusBadge, EmptyState } from '@/components';
import { useQuery, useMutation } from '@/lib/useQuery';
import { formatDate } from '@/lib/format';
import Can from '@rbac/Can';
import { useCrudList } from '../../hooks';
import MailNotConfiguredHint from '../../components/MailNotConfiguredHint';

/** SELL-08 Staff & roles — User / Role lists via crud; invites by one-time join link (D14). */
export default function StaffTab() {
  const { toast } = useToast();
  // Explicit columns: never fetch password hashes into the browser.
  const users = useCrudList('User', { select: ['id', 'name', 'email', 'is_active', 'created_at'], orderBy: { column: 'id', direction: 'asc' }, limit: 200 });
  const roles = useCrudList('Role', { select: ['id', 'name', 'slug', 'description', 'is_system'], orderBy: { column: 'weight', direction: 'desc' }, limit: 100 });
  const invites = useQuery('merchantStaffInvites', {}, { select: (r) => r.data });
  const invite = useMutation('merchantStaffInvite');
  const revoke = useMutation('merchantStaffInviteRevoke');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [link, setLink] = useState(null); // { email, url, mailed, mail_error } — the join link is shown once

  const send = async () => {
    try {
      const res = await invite.run({ email, role });
      setLink({ email: res.data.email, url: res.data.join_url, mailed: res.data.mailed, mail_error: res.data.mail_error });
      toast.success('Invite created', email);
      setEmail('');
      setRole('');
      invites.reload();
    } catch { /* invite.error */ }
  };
  const doRevoke = async (i) => {
    try {
      await revoke.run({ id: i.id });
      toast.success('Invite revoked', i.email);
      invites.reload();
    } catch { /* revoke.error */ }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed', 'Select the link and copy it.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 xl:grid-cols-2">
        <SectionCard title="Staff">
          <QueryState query={users}>
            {(rows) => rows.length ? (
              <Table
                data={rows}
                columns={[
                  { key: 'name', label: 'Name', render: (u) => <div><div className="font-medium">{u.name || '—'}</div><div className="text-xs opacity-70">{u.email}</div></div> },
                  { key: 'is_active', label: 'Status', render: (u) => <StatusBadge status={u.is_active ? 'active' : 'suspended'} label={u.is_active ? 'active' : 'inactive'} /> },
                  { key: 'created_at', label: 'Since', render: (u) => formatDate(u.created_at), hideOnMobile: true },
                ]}
                showSerial={false}
                withAction={false}
                showToolbar={false}
              />
            ) : <EmptyState title="No staff yet" />}
          </QueryState>
        </SectionCard>
        <SectionCard title="Roles">
          <QueryState query={roles}>
            {(rows) => rows.length ? (
              <ul className="divide-y divide-[var(--app-border)]">
                {rows.map((r) => (
                  <li key={r.id} className="py-2">
                    <div className="font-medium flex items-center gap-2">{r.name}{r.is_system ? <StatusBadge status="default" label="system" size="sm" /> : null}</div>
                    {r.description ? <div className="text-sm opacity-70">{r.description}</div> : null}
                  </li>
                ))}
              </ul>
            ) : <EmptyState title="No roles" />}
          </QueryState>
          <p className="text-xs opacity-60">Permissions per role are managed under Data → Roles / Permissions.</p>
        </SectionCard>
      </div>

      <Can module="core" action="create" fallback={<Alert variant="info">Inviting staff needs the user create permission.</Alert>}>
        <SectionCard title="Invite a team member">
          <div className="grid gap-3 sm:grid-cols-[1fr_14rem_auto] items-start">
            <Input type="email" label="Email" placeholder="name@yourstore.in" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Select label="Role" placeholder="Pick a role" options={(roles.data || []).map((r) => ({ value: r.slug, label: r.name }))} value={role} onChange={setRole} closeOnSelect />
            <div className="sm:pt-6"><Button leftIcon={<UserPlus size={16} />} onClick={send} loading={invite.loading} disabled={!email || !role}>Create invite</Button></div>
          </div>
          {invite.error ? <Alert variant="error">{invite.error}</Alert> : null}
          {revoke.error ? <Alert variant="error">{revoke.error}</Alert> : null}
          {link ? (
            <Alert variant="success" title={`Join link for ${link.email}`} dismissible onDismiss={() => setLink(null)}>
              <div className="space-y-2 text-sm">
                <p>{link.mailed ? 'We emailed this link. ' : ''}Share it with them directly too; it works once and expires in 7 days. It will not be shown again.</p>
                {!link.mailed ? <MailNotConfiguredHint reason={link.mail_error} /> : null}
                <code className="block break-all rounded bg-[var(--app-bg)] p-2 text-xs">{link.url}</code>
                <Button size="sm" variant="outline" leftIcon={<Copy size={14} />} onClick={copy}>Copy link</Button>
              </div>
            </Alert>
          ) : null}
          <QueryState query={invites}>
            {(rows) => rows.length ? (
              <ul className="divide-y divide-[var(--app-border)] text-sm">
                {rows.map((i) => (
                  <li key={i.id} className="py-2 flex flex-wrap justify-between gap-2">
                    <span>{i.email} · {i.role}</span>
                    <span className="flex items-center gap-2">
                      <StatusBadge status={i.status} /> <span className="opacity-70">{formatDate(i.sent_at)}</span>
                      {i.status === 'pending' ? <Button size="xs" variant="ghost" onClick={() => doRevoke(i)} loading={revoke.loading}>Revoke</Button> : null}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </QueryState>
        </SectionCard>
      </Can>
    </div>
  );
}
